import { DateTimeSchema } from '@multiplier/lib-shared-date';
import { ValidationError } from '@multiplier/lib-shared-errors';
import { brandedUuid } from '@multiplier/lib-shared-flavor';
import { JsonValueSchema } from '@multiplier/lib-shared-json';
import { z } from 'zod';
import { AuditContextSchema } from './ApiSchemas.js';
export const BusinessMetricIdSchema = brandedUuid('Invalid business metric ID format');
// ============================================================================
// Metric Name Validation
// ============================================================================
/**
 * Metric name format: {team}.{metric}.{submetric}
 * Each part: lowercase alphanumeric with hyphens allowed
 * Examples: platform.free-cash-flow.total, billing.revenue.monthly
 */
const METRIC_NAME_REGEX = /^[a-z0-9-]+\.[a-z0-9-]+\.[a-z0-9-]+$/;
export const MetricNameSchema = z
    .string()
    .min(5, 'Metric name too short')
    .max(255, 'Metric name too long')
    .regex(METRIC_NAME_REGEX, 'Metric name must be in format: team.metric.submetric (lowercase alphanumeric with hyphens)');
/**
 * Parses a metric name into its component parts.
 * @throws ValidationError if the metric name doesn't match the expected format
 */
export function parseMetricName(metricName) {
    const result = MetricNameSchema.safeParse(metricName);
    if (!result.success) {
        throw new ValidationError(`Invalid metric name format: ${metricName}. Expected format: team.metric.submetric (lowercase alphanumeric with hyphens)`, 'metricName');
    }
    const [team, metric, submetric] = metricName.split('.');
    return { team, metric, submetric };
}
// ============================================================================
// Segments Schemas
// ============================================================================
/**
 * Segment values can be strings, numbers, or booleans.
 * Used for dimensional filtering of metrics.
 */
export const MetricSegmentValueSchema = z.union([z.string(), z.number(), z.boolean()]);
/**
 * Segments are key-value pairs for dimensional filtering.
 * Example: { businessContext: "citrine", currency: "GBP", granularity: "month" }
 */
export const MetricSegmentsSchema = z.record(z.string(), MetricSegmentValueSchema).default({});
// ============================================================================
// Request Schemas
// ============================================================================
/**
 * Core fields for upserting a business metric
 */
export const UpsertBusinessMetricDataSchema = z.object({
    /** Metric name in format: team.metric.submetric */
    metricName: MetricNameSchema,
    /** Timestamp for the metric data point (ISO 8601 format) */
    timestamp: DateTimeSchema,
    /** Segments for dimensional filtering (defaults to {}) */
    segments: MetricSegmentsSchema,
    /** The metric value - can be any JSON-compatible value */
    value: JsonValueSchema,
});
/**
 * Request body for upserting a business metric
 */
export const UpsertBusinessMetricRequestSchema = z.object({
    request: UpsertBusinessMetricDataSchema,
    context: AuditContextSchema,
});
/**
 * Core fields for deleting a business metric
 */
export const DeleteBusinessMetricDataSchema = z.object({
    /** Metric name in format: team.metric.submetric */
    metricName: MetricNameSchema,
    /** Timestamp for the metric data point (ISO 8601 format) */
    timestamp: DateTimeSchema,
    /** Segments must match exactly (defaults to {}) */
    segments: MetricSegmentsSchema,
});
/**
 * Request body for deleting a business metric
 */
export const DeleteBusinessMetricRequestSchema = z.object({
    request: DeleteBusinessMetricDataSchema,
    context: AuditContextSchema,
});
/**
 * Core fields for replacing a slice of business metrics.
 *
 * The slice is identified by (team, metric, metricTimestamp, sliceSegments).
 * sliceSegments uses JSONB containment (`segments @> sliceSegments`) — a row
 * is in the slice if its segments contain every key/value in sliceSegments.
 *
 * Existing rows in the slice that are not in `metrics` are deleted; rows in
 * `metrics` are upserted. Pass `metrics: []` to clear the slice entirely.
 */
export const ReplaceMetricsForSliceDataSchema = z.object({
    /** Team component of metric name (e.g., "platform") */
    team: z.string().min(1),
    /** Metric component of metric name (e.g., "free-cash-flow") */
    metric: z.string().min(1),
    /** Timestamp identifying the metric data point */
    metricTimestamp: DateTimeSchema,
    /** Segments that scope the slice — applied as JSONB containment filter */
    sliceSegments: MetricSegmentsSchema,
    /** Full set of metrics for the slice — anything missing here gets deleted */
    metrics: z.array(UpsertBusinessMetricDataSchema),
});
/**
 * Request body for replacing a slice of business metrics
 */
export const ReplaceMetricsForSliceRequestSchema = z.object({
    request: ReplaceMetricsForSliceDataSchema,
    context: AuditContextSchema,
});
/**
 * Response for a slice replace operation
 */
export const ReplaceMetricsForSliceResponseSchema = z.object({
    inserted: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
});
// ============================================================================
// Response Schemas
// ============================================================================
/**
 * Response for a single business metric
 */
export const BusinessMetricResponseSchema = z.object({
    id: BusinessMetricIdSchema,
    metricName: z.string(),
    team: z.string(),
    metric: z.string(),
    submetric: z.string(),
    timestamp: DateTimeSchema,
    segments: MetricSegmentsSchema,
    value: JsonValueSchema,
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
//# sourceMappingURL=BusinessMetricSchemas.js.map